import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, XCircle, PauseCircle, Loader2, ShieldCheck } from 'lucide-react';
import { requisitionsApi } from '../api';

/** iter37 — Warehouse per-line disposition modal.
 *
 *  Every requisition line MUST be given exactly one action: accept / reject / hold.
 *  Reject and Hold require a reason. Rolls up server-side to the requisition-level
 *  status (Accepted / Rejected / Held / PartiallyAccepted) and unlocks Excel export.
 *
 *  Props:
 *    open      — bool
 *    onClose   — fn()
 *    req       — full requisition object (needs `id`, `lines`)
 *    onDone    — fn(freshReq) fired after a successful call
 */
const ACTIONS = [
  { value: 'accept', label: 'Accept', Icon: CheckCircle2, tone: 'text-[#2f6b41] bg-[#E7F2E9] border-[#B4D7B8]', activeTone: 'bg-[#4D7C5F] text-white border-[#4D7C5F]' },
  { value: 'reject', label: 'Reject', Icon: XCircle,      tone: 'text-[#B84A4A] bg-[#FBEFEF] border-[#E8B8B8]', activeTone: 'bg-[#B84A4A] text-white border-[#B84A4A]' },
  { value: 'hold',   label: 'Hold',   Icon: PauseCircle,  tone: 'text-[#8B6A1F] bg-[#FBF3DA] border-[#E8D28C]', activeTone: 'bg-[#B78140] text-white border-[#B78140]' },
];

export default function WarehouseReviewModal({ open, onClose, req, onDone }) {
  const lines = useMemo(() => req?.lines || [], [req]);

  // per-SKU verdict: { [sku]: { action: 'accept'|'reject'|'hold', reason: '' } }
  const [verdicts, setVerdicts] = useState({});
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    // Default every line to Accept so the warehouse only has to touch the exceptions.
    const initial = {};
    lines.forEach(l => { initial[l.sku] = { action: 'accept', reason: '' }; });
    setVerdicts(initial);
    setRemarks('');
    setErr('');
  }, [open, lines]);

  const setLineAction = (sku, action) => {
    setVerdicts(prev => ({ ...prev, [sku]: { ...(prev[sku] || {}), action } }));
  };
  const setLineReason = (sku, reason) => {
    setVerdicts(prev => ({ ...prev, [sku]: { ...(prev[sku] || {}), reason } }));
  };

  const counts = useMemo(() => {
    const c = { accept: 0, reject: 0, hold: 0 };
    Object.values(verdicts).forEach(v => { if (c[v.action] !== undefined) c[v.action] += 1; });
    return c;
  }, [verdicts]);

  const missingReason = useMemo(
    () => lines.some(l => {
      const v = verdicts[l.sku];
      return v && (v.action === 'reject' || v.action === 'hold') && !(v.reason || '').trim();
    }),
    [lines, verdicts],
  );

  if (!open || !req) return null;

  const canSubmit = lines.length > 0 && !missingReason && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr('');
    try {
      const body = {
        lineActions: lines.map(l => ({
          sku: l.sku,
          action: verdicts[l.sku]?.action || 'accept',
          reason: (verdicts[l.sku]?.reason || '').trim() || undefined,
        })),
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      };
      const fresh = await requisitionsApi.warehouseReview(req.id, body);
      onDone?.(fresh);
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Warehouse review failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="warehouse-review-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="fm-card w-full max-w-[820px] bg-white overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E2D3]">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            <ShieldCheck size={16} style={{ color: '#4D7C5F' }} /> Warehouse review · <span className="font-mono">{req.id}</span>
          </div>
          <button onClick={onClose} className="text-[#7a6f5c] hover:text-[#232323]" data-testid="warehouse-review-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[#EFEADC] bg-[#FAF7EE] text-[12.5px] text-[#3d3427]" data-testid="warehouse-review-summary">
          <div className="flex flex-wrap items-center gap-3">
            <span>{lines.length} line{lines.length === 1 ? '' : 's'} to review</span>
            <span className="text-[#4D7C5F]">Accept: <b>{counts.accept}</b></span>
            <span className="text-[#B84A4A]">Reject: <b>{counts.reject}</b></span>
            <span className="text-[#8B6A1F]">Hold: <b>{counts.hold}</b></span>
            <span className="ml-auto text-[11.5px] text-[#7a6f5c]">Rows default to <span className="font-medium">Accept</span> — flip only the exceptions.</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-[13px]" data-testid="warehouse-review-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left text-[11.5px] text-[#7a6f5c] border-b border-[#E7E2D3]">
                <th className="px-4 py-2 font-medium">SKU</th>
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 font-medium text-center">Qty</th>
                <th className="px-4 py-2 font-medium">Condition</th>
                <th className="px-4 py-2 font-medium">Verdict</th>
                <th className="px-4 py-2 font-medium">Reason (for Reject / Hold)</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const v = verdicts[l.sku] || { action: 'accept', reason: '' };
                const needsReason = v.action === 'reject' || v.action === 'hold';
                const reasonMissing = needsReason && !(v.reason || '').trim();
                return (
                  <tr key={`${l.sku}-${i}`} className="border-t border-[#EFEADC]" data-testid={`warehouse-review-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-[12.5px]">{l.sku}</td>
                    <td className="px-4 py-2">{l.product}</td>
                    <td className="px-4 py-2 text-center">{l.qty}</td>
                    <td className="px-4 py-2 text-[12.5px] capitalize">{l.condition || 'new'}</td>
                    <td className="px-4 py-2">
                      <div className="inline-flex gap-1 flex-wrap">
                        {ACTIONS.map(a => {
                          const active = v.action === a.value;
                          const Icon = a.Icon;
                          return (
                            <button
                              key={a.value}
                              type="button"
                              onClick={() => setLineAction(l.sku, a.value)}
                              data-testid={`warehouse-review-action-${i}-${a.value}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] border transition-colors ${active ? a.activeTone : a.tone}`}
                            >
                              <Icon size={11} /> {a.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        data-testid={`warehouse-review-reason-${i}`}
                        type="text"
                        value={v.reason}
                        onChange={e => setLineReason(l.sku, e.target.value)}
                        placeholder={needsReason ? `Reason required for ${v.action}` : '—'}
                        disabled={!needsReason}
                        className={`fm-focus w-full border rounded-md px-2 py-1 text-[12.5px] bg-white ${reasonMissing ? 'border-[#B84A4A]' : 'border-[#E7E2D3]'} disabled:bg-[#F7F4EA] disabled:text-[#a6997f]`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-[#EFEADC] bg-white">
          <label htmlFor="warehouse-review-remarks" className="block text-[11.5px] font-medium text-[#3d3427] mb-1">
            Warehouse remarks <span className="text-[#7a6f5c] font-normal">(optional — visible to EBO & ops)</span>
          </label>
          <textarea
            id="warehouse-review-remarks"
            data-testid="warehouse-review-remarks"
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder="Anything the EBO / ops team should know about this parcel…"
            maxLength={500}
            rows={2}
            className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[12.5px] bg-white"
          />
          {err && <div className="text-[12px] text-[#B84A4A] mt-2" data-testid="warehouse-review-error">{err}</div>}
          {missingReason && (
            <div className="text-[11.5px] text-[#B84A4A] mt-1">Provide a reason for every Reject / Hold line before submitting.</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E7E2D3] bg-[#FAF7EE]">
          <button
            onClick={onClose}
            data-testid="warehouse-review-cancel"
            className="px-3 py-1.5 rounded-md text-[13px] border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="warehouse-review-submit"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-white text-[13px] font-medium disabled:opacity-50"
            style={{ background: canSubmit ? '#4D7C5F' : '#7a6f5c' }}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            Submit review
          </button>
        </div>
      </div>
    </div>
  );
}
